import { useEffect, useMemo } from 'react'
import useSWR, { mutate } from 'swr'
import { useStaticData } from '@mc/static-data-react'
import { paths } from '@mc/paths'
import { type Course as ApiCourse } from '@mc/api'

import {
  tileOptimizedImage,
  nameplateOptimizedImage,
  type CourseTile,
} from 'components/GlobalCarousel'
import { pruneRelatedCourses } from 'libs/utils'
import { type MarketingDataType, type MarketingSectionItem } from 'types'
import { CourseSubType, type Category, type Course } from 'types/data'
import { useAltCategoriesFetcher } from 'experiments/pen-2866_v1_alternative_categories/swrFetcher'
import {
  useAllSubcategoriesDeprecated,
  useCoursesBySlugs,
} from 'libs/client/api'

import { TRENDING as TRENDING_SLUG } from 'libs/utils/categories'

export type ConcreteCategory = {
  [key in keyof Pick<Category, 'id' | 'name' | 'slug'>]-?: Required<
    NonNullable<Category[key]>
  >
}

export const useCategories = () => {
  const { categories: serverCategories } = useStaticData<{
    categories: Category[]
  }>()

  if (!serverCategories) throw new Error('no server categories')

  return useMemo(() => {
    const categories = serverCategories?.filter(
      (c: Category): c is ConcreteCategory => !!c.id && !!c.slug && !!c.name,
    )
    if (categories.length === 0) throw new Error('no categories')
    return categories
  }, [serverCategories])
}

type UseCourseTilesArgs = {
  selectedCategorySlug: string
  trendingItems: MarketingDataType['marketingSectionItems']
}

export const useCourseTiles = ({
  selectedCategorySlug,
  trendingItems,
}: UseCourseTilesArgs) => {
  const categories = useCategories()

  const slug =
    selectedCategorySlug === TRENDING_SLUG
      ? categories[0].slug
      : selectedCategorySlug

  const data = useCoursesByCategory(slug)
  const courses: Course[] = data.coursesByCategory ?? []

  const trending = trendingToCourseTiles(trendingItems)

  const courseTiles =
    selectedCategorySlug === TRENDING_SLUG
      ? trending
      : coursesToCourseTiles(courses)

  // initial preload for all of the "first pages" of the carousel
  // all courses by categories + first 4 tile images
  usePreloadAllCategorySlugs()

  // when the course tiles change, preload all images
  // otherwise they'd be lazy loaded as the scroll occurs; not ideal
  usePreloadAllImagesForCourseTiles(courseTiles)

  const loading = !courses || data.isValidating
  const error = !!data.error

  return { loading, error, courseTiles }
}

type UseSubcatCourseTilesArgs = {
  subcatCourses: string[]
}

export const useSubcatCourseTilesDeprecated = ({
  subcatCourses,
}: UseSubcatCourseTilesArgs) => {
  const { coursesBySlugs, error, isLoading } = useCoursesBySlugs(subcatCourses)

  const courseTiles = useMemo(
    () => coursesToCourseTiles((coursesBySlugs as Course[]) || []),
    [coursesBySlugs],
  )

  // initial preload for all of the "first pages" of the carousel
  // all courses by subcategories + first 4 tile images
  usePreloadAllSubcategorySlugs()

  // when the course tiles change, preload all images
  // otherwise they'd be lazy loaded as the scroll occurs; not ideal
  usePreloadAllImagesForCourseTiles(courseTiles)

  return { loading: isLoading, error: !!error, courseTiles }
}

const getCoursesByCategoryURL = (slug: string) =>
  `/jsonapi/v1/courses?filter[category]=${slug}`

const getCoursesByCourseSlugsURL = (courseSlugs: string[]) => {
  const joinedSlugs = courseSlugs?.join(',') || ''
  return `/jsonapi/v1/courses?filter[slug]=${encodeURIComponent(joinedSlugs)}`
}

export const useCoursesByCategory = (slug: string) => {
  const url = getCoursesByCategoryURL(slug)
  const fetcher = useAltCategoriesFetcher()
  const { data, isValidating, error } = useSWR<ApiCourse[]>(url, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  const coursesByCategory = useMemo(
    () => data && pruneRelatedCourses(data),
    [data],
  )

  return {
    coursesByCategory,
    isValidating,
    error,
  }
}

/**
 * Safari does not support `requestIdleCallback`
 */
const invokeWhenIdle = (cb: () => void): (() => void) => {
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    const handle = window.requestIdleCallback(cb)
    return () => window.cancelIdleCallback(handle)
  } else {
    const handle = setTimeout(cb, 20)
    return () => clearTimeout(handle)
  }
}

const usePreloadAllCategorySlugs = () => {
  const categories = useCategories()
  const fetcher = useAltCategoriesFetcher()

  useEffect(() => {
    let cancel: () => void | null
    categories.forEach((category) => {
      cancel = invokeWhenIdle(async () => {
        // `mutate`: fetch and cache pattern
        // https://sergiodxa.com/articles/next-swr-prefetch
        const url = getCoursesByCategoryURL(category.slug)
        const courses: Course[] | undefined = await fetcher(url)
        mutate(url, courses, false)
        courses
          ?.slice(0, 4)
          .map((course) => ({
            ...course,
            primary2x3: course.primary2x3 ?? undefined,
            nameplateImage: course.nameplate ?? undefined,
          }))
          .forEach(preloadCourseImages)
      })
    })
    return () => {
      cancel?.()
    }
  }, [categories, fetcher])

  return null
}

const usePreloadAllSubcategorySlugs = () => {
  const subcatsData = useAllSubcategoriesDeprecated()
  const subcats = Object.entries(subcatsData?.subcats ?? {})
    .map(([, v]) => v.subcategories)
    .flat()
  const fetcher = useAltCategoriesFetcher()

  useEffect(() => {
    let cancel: () => void | null
    subcats.forEach((subcat) => {
      cancel = invokeWhenIdle(async () => {
        // `mutate`: fetch and cache pattern
        // https://sergiodxa.com/articles/next-swr-prefetch
        const url = getCoursesByCourseSlugsURL(subcat.courses)
        const courses: Course[] | undefined = await fetcher(url)
        mutate(url, courses, false)
        courses
          ?.slice(0, 4)
          .map((course) => ({
            ...course,
            primary2x3: course.primary2x3 ?? undefined,
            nameplateImage: course.nameplate ?? undefined,
          }))
          .forEach(preloadCourseImages)
      })
    })
    return () => {
      cancel?.()
    }
  }, [subcats, fetcher])

  return null
}

const usePreloadAllImagesForCourseTiles = (courseTiles: CourseTile[]) => {
  useEffect(() => {
    courseTiles.forEach((tileData) => {
      preloadCourseImages(tileData)
    })
  }, [courseTiles])
}

const preloadCourseImages = ({
  primary2x3,
  nameplateImage,
}: Partial<Pick<CourseTile, 'primary2x3' | 'nameplateImage'>>) => {
  if (primary2x3) {
    const tileImgSrc = tileOptimizedImage(primary2x3)
    preloadImage(tileImgSrc)
  }
  if (nameplateImage) {
    const nameplateImageSrc = nameplateOptimizedImage(nameplateImage)
    preloadImage(nameplateImageSrc)
  }
}

const preloadImage = (src: string | undefined | null) => {
  if (src) {
    new Image().src = src
  }
}

// TODO remove all these non-null assertions
export const coursesToCourseTiles = (courses: Course[]): CourseTile[] =>
  courses.map((course) => ({
    ...course,
    id: String(course.id || 0),
    slug: course.slug ?? '',
    title: course.displaySubtitle ?? '',
    headline: course.headline ?? '',
    primaryCategoryId: course.primaryCategory?.id,
    nameplateImage: course.nameplate ?? '',
    subtitle: course.displaySubtitle ?? '',
    primary2x3: course.primary2x3 ?? '',
    duration:
      (course.type === CourseSubType.SERIES
        ? course.numChapters
        : course.totalSeconds) ?? 0,
    tag: course.tag ?? '',
    releaseDescription: course.releaseDescription ?? null,
    link: paths.courseTypePage(course.type, course.slug ?? ''),
  }))

const trendingToCourseTiles = (
  trending: MarketingSectionItem[],
): CourseTile[] =>
  trending.map((course) => ({
    ...course,
    headline: course.title,
    type: course.type || 'course',
    primary2x3: course.primary2x3 ?? '',
    nameplateImage: course.nameplateImage ?? '',
  }))
